import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link as RouterLink, createSearchParams } from 'react-router-dom';
import { FIREBASE_STORAGE_PATTERN_LIBRARY_URL } from '../../config/constants';
import { Chips } from './Chips';
import { RADIUS_SM, RADIUS_MD, SHADOW_MODAL } from '../../styles/tokens';
import { buttonTypographySx } from '../../styles/patterns';

const CardContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	border-radius: ${RADIUS_SM};
	width: 100%;
	box-shadow: 0 8px 8px rgba(0, 0, 0, 0.3);
`;

const CardImageContainer = styled.div`
	display: flex;
	justify-content: center;
	border-radius: ${RADIUS_SM} ${RADIUS_SM} 0 0;
	overflow: hidden;
`;

const CardImage = styled.img`
	flex: 1;
	width: 100%;
	height: 200px;
	object-fit: cover;
	cursor: zoom-in;
`;

const CardInfo = styled.div`
	display: flex;
	flex-direction: column;
	padding: 15px;
	border-radius: 0 0 ${RADIUS_SM} ${RADIUS_SM};
`;

const CardInfoHorizontal = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
`;

const PreviewOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 9999;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.7);
	backdrop-filter: blur(2px);
	opacity: 0;
	visibility: hidden;
	transition:
		opacity 180ms ease,
		visibility 180ms ease;

	&.open {
		opacity: 1;
		visibility: visible;
	}
`;

const PreviewContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	max-width: 80vw;
	max-height: 80vh;
	background: #fff;
	border-radius: ${RADIUS_MD};
	box-shadow: ${SHADOW_MODAL};
	overflow: hidden;
	animation: fadeUp 200ms ease;

	@keyframes fadeUp {
		from {
			transform: translateY(10px);
			opacity: 0;
		}
		to {
			transform: translateY(0);
			opacity: 1;
		}
	}
`;

const PreviewImages = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	background: #000;
	overflow: hidden;
	max-height: 60vh;
`;

const PreviewImage = styled.img`
	flex: 1;
	max-width: 100%;
	max-height: 60vh;
	object-fit: contain;
	cursor: zoom-out;
`;

export const PatternCard = (props) => {
	const { pattern } = props;
	const { name, amount, fileName, fileName2, tags } = pattern;

	const [imageUrl, setImageUrl] = useState('');
	const [imageUrl2, setImageUrl2] = useState('');

	const [previewOpen, setPreviewOpen] = useState(false);
	const overlayRef = useRef(null);

	useEffect(() => {
		const fetchImage = async (name, callback) => {
			if (!name) return;
			const baseUrl = FIREBASE_STORAGE_PATTERN_LIBRARY_URL;
			const metaUrl = `${baseUrl}${name}.jpg`;
			const res = await fetch(metaUrl);
			if (!res.ok) return;
			const metaInfo = await res.json();
			callback(`${metaUrl}?alt=media&token=${metaInfo.downloadTokens}`);
		};
		fetchImage(fileName, setImageUrl);
		fetchImage(fileName2, setImageUrl2);
	}, [fileName, fileName2]);

	const toPatternMaker = useMemo(() => {
		const params = { a: fileName };
		if (fileName2) params.b = fileName2;
		return {
			pathname: '/patternmaker',
			search: createSearchParams(params).toString(),
		};
	}, [fileName, fileName2]);

	const formatAmount = () =>
		!amount ? 'Free' : `$${(amount / 100).toFixed(2)}`;

	useEffect(() => {
		if (!previewOpen) return;
		const onKeyDown = (e) => {
			if (e.key === 'Escape') setPreviewOpen(false);
		};
		document.addEventListener('keydown', onKeyDown);
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.body.style.overflow = prev;
		};
	}, [previewOpen]);

	const openPreview = () => setPreviewOpen(true);
	const closePreview = () => setPreviewOpen(false);

	const onOverlayClick = (e) => {
		// close only if backdrop (not the image) is clicked
		if (e.target === overlayRef.current) closePreview();
	};

	function formatPatternName() {
		if (!name) return '';
		return name
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	return (
		<>
			<CardContainer>
				<CardImageContainer>
					<CardImage
						src={imageUrl}
						alt={name}
						onClick={openPreview}
						aria-haspopup="dialog"
						aria-expanded={previewOpen}
					/>
					{imageUrl2 && (
						<CardImage
							src={imageUrl2}
							alt={name}
							onClick={openPreview}
							aria-haspopup="dialog"
							aria-expanded={previewOpen}
						/>
					)}
				</CardImageContainer>

				<CardInfo>
					<Typography
						variant="h3"
						noWrap
						style={{
							marginBottom: '-10px',
							marginTop: '-15px',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						}}
					>
						{formatPatternName()}
					</Typography>

					{tags?.length ? <Chips tags={tags} /> : null}

					<CardInfoHorizontal>
						<Typography
							variant="h4"
							style={{ marginBottom: '-15px' }}
						>
							{formatAmount()}
						</Typography>
						<Button
							component={RouterLink}
							to={toPatternMaker}
							target="_blank"
							rel="noopener noreferrer"
							variant="contained"
							size="small"
							color="success"
						>
							<Typography variant="h5" sx={buttonTypographySx}>
								Begin
							</Typography>
						</Button>
					</CardInfoHorizontal>
				</CardInfo>
			</CardContainer>

			<PreviewOverlay
				ref={overlayRef}
				className={previewOpen ? 'open' : ''}
				role="dialog"
				aria-modal="true"
				aria-label={`${name} preview`}
				onClick={onOverlayClick}
			>
				<PreviewContent>
					<PreviewImages>
						<PreviewImage
							src={imageUrl}
							alt={`${name} preview`}
							onClick={closePreview}
						/>
						{imageUrl2 && (
							<PreviewImage
								src={imageUrl2}
								alt={`${name} preview 2`}
								onClick={closePreview}
							/>
						)}
					</PreviewImages>

					<CardInfo style={{ borderRadius: 0 }}>
						<Typography
							variant="h3"
							noWrap
							style={{
								marginBottom: '-10px',
								marginTop: '-15px',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{formatPatternName()}
						</Typography>

						{tags && (
							<Stack
								direction="row"
								spacing={0.5}
								style={{ marginBottom: '15px' }}
							>
								{tags.map((tag, i) => (
									<Chip
										key={i}
										label={tag}
										color="primary"
										size="small"
										sx={{
											'& .MuiChip-label': {
												fontSize: '1.2rem',
												marginBottom: '-2px',
											},
										}}
									/>
								))}
							</Stack>
						)}

						<CardInfoHorizontal>
							<Typography
								variant="h4"
								style={{ marginBottom: '-15px' }}
							>
								{formatAmount()}
							</Typography>

							<Button
								component={RouterLink}
								to={toPatternMaker}
								target="_blank"
								rel="noopener noreferrer"
								variant="contained"
								size="small"
								color="success"
							>
								<Typography
									variant="h5"
									sx={buttonTypographySx}
								>
									Begin
								</Typography>
							</Button>
						</CardInfoHorizontal>
					</CardInfo>
				</PreviewContent>
			</PreviewOverlay>
		</>
	);
};
